import axios from 'axios';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import type {
  RecipeDiffRow,
  RecipeSnapshot,
  RecipeVersionDetail,
  RecipeVersionList,
} from '../types/recipeVersion.types';

export class RecipeVersionApiError extends Error {
  code?: string;
  diff?: RecipeDiffRow[];
  missingItems?: string[];

  constructor(message: string, code?: string, diff?: RecipeDiffRow[], missingItems?: string[]) {
    super(message);
    this.name = 'RecipeVersionApiError';
    this.code = code;
    this.diff = diff;
    this.missingItems = missingItems;
  }
}

interface ErrorBody {
  message?: string;
  code?: string;
  diff?: RecipeDiffRow[];
  missingItems?: string[];
}

function toError(error: unknown): RecipeVersionApiError {
  if (error instanceof RecipeVersionApiError) return error;
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ErrorBody | undefined;
    return new RecipeVersionApiError(
      data?.message || 'Beklenmeyen bir hata oluştu.',
      data?.code,
      data?.diff,
      data?.missingItems
    );
  }
  return new RecipeVersionApiError('Beklenmeyen bir hata oluştu.');
}

function payloadOf(snapshot: RecipeSnapshot) {
  return {
    SchemaVersion: snapshot.schemaVersion || 1,
    Header: {
      RecipeName: snapshot.header.recipeName,
      RecipeCode: snapshot.header.recipeCode ?? null,
      Description: snapshot.header.description ?? null,
      OutputQuantity: snapshot.header.outputQuantity,
      OutputUnit: snapshot.header.outputUnit,
      IsActive: snapshot.header.isActive,
      BaseIngredientItemId: snapshot.header.baseIngredientItemId ?? null,
    },
    Ingredients: (snapshot.ingredients || []).map((row) => ({
      IngredientItemId: row.ingredientItemId,
      IngredientName: row.ingredientName ?? null,
      IngredientStockCode: row.ingredientStockCode ?? null,
      Quantity: row.quantity,
      Unit: row.unit,
      WastagePercent: row.wastagePercent,
      ExplodeOverride: row.explodeOverride ?? null,
      EntryUnit: row.entryUnit ?? null,
      EntryQuantity: row.entryQuantity ?? null,
      EntryFactor: row.entryFactor ?? null,
      SortOrder: row.sortOrder,
      Notes: row.notes ?? null,
      IsActive: row.isActive,
      RoutingStepNo: row.routingStepNo ?? null,
    })),
    Outputs: (snapshot.outputs || []).map((row) => ({
      ItemId: row.itemId,
      ItemName: row.itemName ?? null,
      Unit: row.unit,
      OutputType: row.outputType,
      Quantity: row.quantity,
      CostMethod: row.costMethod,
      CostSharePercent: row.costSharePercent ?? null,
      FixedUnitValue: row.fixedUnitValue ?? null,
      SortOrder: row.sortOrder,
    })),
    RoutingSteps: (snapshot.routingSteps || []).map((row) => ({
      StepNo: row.stepNo,
      OperationId: row.operationId ?? null,
      Name: row.name,
      WorkCenterId: row.workCenterId ?? null,
      SetupMinutes: row.setupMinutes,
      RunMinutesPerUnit: row.runMinutesPerUnit,
      Instructions: row.instructions ?? null,
      Checks: (row.checks || []).map((check, index) => ({
        Name: check.name,
        CheckType: check.checkType,
        Unit: check.unit ?? null,
        MinValue: check.minValue ?? null,
        MaxValue: check.maxValue ?? null,
        TargetValue: check.targetValue ?? null,
        IsRequired: check.isRequired,
        OnFail: check.onFail,
        Instructions: check.instructions ?? null,
        SortOrder: check.sortOrder ?? index,
      })),
    })),
  };
}

export async function listRecipeVersions(recipeId: string): Promise<RecipeVersionList> {
  try {
    const response = await accountingApi.get<RecipeVersionList>(ACCOUNTING_API.RECIPE_VERSIONS(recipeId));
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function getRecipeVersion(recipeId: string, versionNo: number): Promise<RecipeVersionDetail> {
  try {
    const response = await accountingApi.get<RecipeVersionDetail>(ACCOUNTING_API.RECIPE_VERSION(recipeId, versionNo));
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function getRecipeDraft(recipeId: string): Promise<RecipeVersionDetail> {
  try {
    const response = await accountingApi.get<RecipeVersionDetail>(ACCOUNTING_API.RECIPE_VERSION_DRAFT(recipeId));
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function snapshotRecipeVersion(recipeId: string, note?: string): Promise<number> {
  try {
    const response = await accountingApi.post<{ versionNo: number }>(
      ACCOUNTING_API.RECIPE_VERSION_SNAPSHOT(recipeId),
      { Note: note ?? '' }
    );
    return response.data.versionNo;
  } catch (error) {
    throw toError(error);
  }
}

export async function createRecipeDraft(recipeId: string, fromVersionNo?: number | null): Promise<void> {
  try {
    await accountingApi.post(ACCOUNTING_API.RECIPE_VERSION_DRAFT_CREATE(recipeId), {
      FromVersionNo: fromVersionNo ?? null,
    });
  } catch (error) {
    throw toError(error);
  }
}

export async function saveRecipeDraft(recipeId: string, snapshot: RecipeSnapshot, note?: string): Promise<void> {
  try {
    await accountingApi.post(ACCOUNTING_API.RECIPE_VERSION_DRAFT_SAVE(recipeId), {
      Snapshot: payloadOf(snapshot),
      Note: note ?? '',
    });
  } catch (error) {
    throw toError(error);
  }
}

export async function publishRecipeDraft(recipeId: string, force: boolean, note?: string): Promise<number> {
  try {
    const response = await accountingApi.post<{ versionNo: number }>(
      ACCOUNTING_API.RECIPE_VERSION_PUBLISH(recipeId),
      { Force: force, Note: note ?? '' }
    );
    return response.data.versionNo;
  } catch (error) {
    throw toError(error);
  }
}

export async function discardRecipeDraft(recipeId: string): Promise<void> {
  try {
    await accountingApi.post(ACCOUNTING_API.RECIPE_VERSION_DISCARD(recipeId), {});
  } catch (error) {
    throw toError(error);
  }
}

export async function restoreRecipeVersion(recipeId: string, versionNo: number, note?: string): Promise<number> {
  try {
    const response = await accountingApi.post<{ versionNo: number }>(
      ACCOUNTING_API.RECIPE_VERSION_RESTORE(recipeId),
      { VersionNo: versionNo, Note: note ?? '' }
    );
    return response.data.versionNo;
  } catch (error) {
    throw toError(error);
  }
}

export async function compareRecipeVersions(recipeId: string, a: string, b: string): Promise<RecipeDiffRow[]> {
  try {
    const response = await accountingApi.post<RecipeDiffRow[]>(
      ACCOUNTING_API.RECIPE_VERSION_COMPARE(recipeId),
      { A: a, B: b }
    );
    return response.data || [];
  } catch (error) {
    throw toError(error);
  }
}
